import { useCallback, useEffect, useRef, useState } from "react";
import type { EconHeaderCountdownEvent } from "../types/econ-header";
import { getRuntimeApiBase } from "../lib/runtime-api-base";

const POLL_INTERVAL_MS = 5_000;
const SNAPSHOT_STORAGE_KEY = "fintheon:econ-header-countdown:snapshot:v1";

export function useEconHeaderCountdown() {
  const [events, setEvents] = useState<EconHeaderCountdownEvent[]>(() =>
    readStoredEvents(),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(async () => {
    try {
      const res = await fetch(
        `${getRuntimeApiBase()}/api/econ/header-countdown`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as {
        events?: EconHeaderCountdownEvent[];
      };
      if (!mountedRef.current) return;
      const nextEvents = Array.isArray(json.events) ? json.events : [];
      setEvents(nextEvents);
      writeStoredEvents(nextEvents);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : "Fetch failed");
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
    const interval = window.setInterval(() => void refetch(), POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [refetch]);

  useEffect(() => {
    const handleRefresh = () => void refetch();
    window.addEventListener(
      "fintheon:econ-header-countdown-refresh",
      handleRefresh,
    );
    return () => {
      window.removeEventListener(
        "fintheon:econ-header-countdown-refresh",
        handleRefresh,
      );
    };
  }, [refetch]);

  useEffect(() => {
    const stream = new EventSource(
      `${getRuntimeApiBase()}/api/riskflow/stream`,
    );
    const handleEconPrint = () => void refetch();
    stream.addEventListener("econ-print", handleEconPrint);
    return () => {
      stream.removeEventListener("econ-print", handleEconPrint);
      stream.close();
    };
  }, [refetch]);

  return { events, isLoading, error, refetch };
}

function readStoredEvents(): EconHeaderCountdownEvent[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(SNAPSHOT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { events?: unknown };
    return Array.isArray(parsed.events)
      ? parsed.events.filter(isCountdownEvent)
      : [];
  } catch {
    return [];
  }
}

function writeStoredEvents(events: EconHeaderCountdownEvent[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      SNAPSHOT_STORAGE_KEY,
      JSON.stringify({ events, savedAt: new Date().toISOString() }),
    );
  } catch {
    /* ignore storage failures */
  }
}

function isCountdownEvent(value: unknown): value is EconHeaderCountdownEvent {
  if (!value || typeof value !== "object") return false;
  const event = value as Partial<EconHeaderCountdownEvent>;
  return (
    typeof event.id === "string" &&
    typeof event.dayPlanWindowId === "string" &&
    typeof event.eventName === "string" &&
    typeof event.scheduledAt === "string"
  );
}
